import type { Me, Role } from '@/shared/types/domain'

/**
 * Matriz rol x accion.
 *
 * Segunda linea de defensa despues de los guards: el guard protege la
 * NAVEGACION, `can()` protege la UI (botones, columnas, acciones de fila). La
 * tercera y definitiva es el backend (RLS + routers separados): esto es
 * ergonomia, no seguridad.
 *
 * Regla de oro: **todo nace interno**. Si una accion no esta listada para un
 * rol, esta prohibida. El default nunca es permitir.
 */

export type Action =
  // Portafolio y navegacion interna
  | 'portfolio:read'
  | 'board:read'
  // Empresas
  | 'company:read'
  | 'company:write'
  | 'company:create'
  | 'contact:write'
  // Proyectos
  | 'project:read'
  | 'project:write'
  | 'project:create'
  | 'project:reorder'
  | 'project:toggle_client_visibility'
  // Tareas
  | 'task:read'
  | 'task:write'
  | 'task:create'
  | 'task:reorder'
  // Accesos
  | 'access:read'
  | 'access:write'
  | 'access:reveal'
  // Documentos
  | 'document:read'
  | 'document:write'
  // Arquitectura y repos
  | 'architecture:read'
  | 'architecture:write'
  | 'repository:read'
  | 'repository:write'
  // Configuracion
  | 'settings:read'
  | 'settings:write'
  | 'user:manage'
  | 'job:run'
  // Portal
  | 'portal:read'
  | 'portal:view_as_client'

const TECH_MEMBER: readonly Action[] = [
  'portfolio:read',
  'board:read',
  'company:read',
  'company:write',
  'contact:write',
  'project:read',
  'project:write',
  'project:create',
  'project:reorder',
  'project:toggle_client_visibility',
  'task:read',
  'task:write',
  'task:create',
  'task:reorder',
  'access:read',
  'access:write',
  'access:reveal',
  'document:read',
  'document:write',
  'architecture:read',
  'architecture:write',
  'repository:read',
  'repository:write',
  'settings:read',
]

const ADMIN: readonly Action[] = [
  ...TECH_MEMBER,
  'company:create',
  'settings:write',
  'user:manage',
  'job:run',
  'portal:view_as_client',
]

/**
 * El cliente es READ-ONLY ABSOLUTO y solo dentro del portal. No tiene
 * `access:read` ni `access:reveal` (no existe la ruta siquiera) ni
 * `project:reorder` (el orden del portafolio es una decision interna).
 */
const CLIENT: readonly Action[] = ['portal:read']

const MATRIX: Record<Role, ReadonlySet<Action>> = {
  admin: new Set(ADMIN),
  tech_member: new Set(TECH_MEMBER),
  client: new Set(CLIENT),
}

export interface ResourceScope {
  companyId?: string | null
}

/**
 * Valida el rol que llega del backend. Es el MISMO vocabulario a los dos lados
 * (`admin` / `tech_member` / `client`): no hay traduccion, a proposito. Dos
 * nombres para el mismo concepto es lo que despues produce el bug de "pero yo
 * habia preguntado por client_contact".
 *
 * El rol sale de `GET /me`, NUNCA de leer el JWT.
 */
export function normalizeRole(raw: string | null | undefined): Role | null {
  return raw === 'admin' || raw === 'tech_member' || raw === 'client' ? raw : null
}

export function roleCan(role: Role | null | undefined, action: Action): boolean {
  if (!role) return false
  return MATRIX[role]?.has(action) ?? false
}

/**
 * `can(me, action, resource?)`.
 *
 * Con `resource.companyId`, ademas del rol se verifica la membresia: un
 * `tech_member` solo opera sobre las empresas que tiene asignadas. El admin ve
 * todo y el cliente solo su propia empresa.
 */
export function can(me: Me | null | undefined, action: Action, resource?: ResourceScope): boolean {
  if (!me) return false

  const role = normalizeRole(me.role)
  if (!roleCan(role, action)) return false

  const companyId = resource?.companyId
  if (!companyId) return true

  if (role === 'admin') return true
  if (role === 'client') return me.client_company_id === companyId
  return me.company_ids.includes(companyId)
}

export function isInternal(me: Me | null | undefined): boolean {
  const role = normalizeRole(me?.role)
  return role === 'admin' || role === 'tech_member'
}

export function isClient(me: Me | null | undefined): boolean {
  return normalizeRole(me?.role) === 'client'
}

export function isAdmin(me: Me | null | undefined): boolean {
  return normalizeRole(me?.role) === 'admin'
}

/** Landing por rol: el cliente nunca aterriza en una pantalla interna. */
export function homePathFor(me: Me | null | undefined): string {
  return isClient(me) ? '/portal' : '/portfolio'
}
